import { formatAmount, parseAmount } from './index';
import { EnteredValue, OrderedRow, SavedValue } from '../models/priceRows.model';
import { QuotationVersion } from '../models/quotationVersion.model';
import { visibleRows } from './priceFieldRows';
import { RowValue, computePriceRowValues } from './priceRowValues';

export type PriceRow = {
  id: string;
  name: string;
  price?: string;
  unit?: string;
  rawPrice?: number;
};

export type PriceTable = {
  rows: OrderedRow[];
  enteredValues: Record<string, EnteredValue>;
  budget?: number;
};

export const valueKey = (customRowId: string, supplierId: string, refVersion: number) => `${customRowId}:${supplierId}:${refVersion}`;

export const enteredValuesOf = (values: SavedValue[]): Record<string, EnteredValue> =>
  Object.fromEntries(values.map(({ customRow, supplierId, refVersion, value, text }) =>
    [valueKey(customRow, supplierId, refVersion), { value, text }]));

export const priceTableOf = (
  rows: OrderedRow[],
  enteredValues: Record<string, EnteredValue>,
  budgetText?: string,
): PriceTable => ({
  rows,
  enteredValues,
  budget: budgetText ? parseAmount(budgetText) : undefined,
});

const hasBidOn = (version: QuotationVersion, supplierId: string) =>
  !!version.suppliers?.some(s => s.supplierId === supplierId) &&
  !!version.supplierBids?.some(b => b.supplierId === supplierId);

export const latestBidVersion = (
  versions: QuotationVersion[],
  supplierId: string,
): QuotationVersion | undefined => {
  let latest: QuotationVersion | undefined;
  for (const version of versions) {
    if (hasBidOn(version, supplierId) && (!latest || version.refVersion > latest.refVersion)) {
      latest = version;
    }
  }
  return latest;
};

const priceFieldsAcrossVersions = (versions: QuotationVersion[]) => {
  const fields = new Map<string, { name?: string, unit?: string }>();
  for (const version of versions) {
    for (const detail of version?.priceDetails ?? []) {
      const id = detail?.priceType?._id;
      if (!id || fields.has(String(id))) continue;
      fields.set(String(id), { name: detail.priceType?.name, unit: detail.unit });
    }
  }
  return fields;
};

export const bidPrices = (versions: QuotationVersion[], versionIdx: number, supplierId: string, fallbackUnit: string): PriceRow[] => {
  const version = versions[versionIdx];
  if (!version) return [];
  const supplierBid = version.supplierBids?.find(x => x.supplierId === supplierId);
  const priceDetails = (version.priceDetails ?? [])
    .flatMap(({ priceType, unit }) => (priceType ? [{ priceType, unit }] : []));

  if (supplierBid?.priceDetails) {
    const otherVersions = priceFieldsAcrossVersions(versions);
    return supplierBid.priceDetails.map(bid => {
      const formattedPrice = formatAmount(bid.price ?? '');
      const priceDetail = priceDetails.find(details => details.priceType._id == bid.priceType);
      const definedElsewhere = priceDetail ? undefined : otherVersions.get(String(bid.priceType));
      return {
        id: priceDetail?.priceType._id ?? bid.priceType,
        name: priceDetail?.priceType.name ?? definedElsewhere?.name ?? '',
        unit: priceDetail?.unit ?? definedElsewhere?.unit ?? fallbackUnit,
        price: formattedPrice ? formattedPrice : '-',
        rawPrice: parseFloat(String(bid.price)),
      };
    });
  }
  return priceDetails.map(({ priceType }) => ({ id: priceType._id, name: priceType.name }));
};

export const bidRowValues = (
  { rows, enteredValues, budget }: PriceTable,
  prices: PriceRow[],
  supplierId: string,
  refVersion: number,
): Map<string, RowValue> => {
  const enteredPrices = rows.filter(row => row.kind === 'value').map(row => ({
    id: row.id,
    amount: enteredValues[valueKey(row.id, supplierId, refVersion)]?.value ?? undefined,
  }));
  const supplierPrices = prices.map(({ id, rawPrice, unit }) => ({ id, amount: rawPrice, unit }));
  const shownRows = visibleRows(rows, new Set(prices.map(price => price.id)));
  return computePriceRowValues(shownRows, [...supplierPrices, ...enteredPrices], budget);
};

export const bidTotal = (table: PriceTable, prices: PriceRow[], supplierId: string, refVersion: number): RowValue | undefined => {
  const sumRow = table.rows.find(row => row.kind === 'sum');
  return sumRow ? bidRowValues(table, prices, supplierId, refVersion).get(sumRow.id) : undefined;
};
